/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Scroll-based active-section detection for the landing page header. Tracks
 * which section is currently in view so the nav can highlight it.
 */

import { useEffect, useState } from 'react';
import { FF_EARLY_ACCESS } from '@/shared/utils/feature-flag';

/**
 * Resolves the landing-page section ids in scroll order, varying by whether the
 * early-access variant of the landing page is active.
 *
 * @returns The ordered section id list to track.
 */
function resolveSectionIds(): string[] {
  return FF_EARLY_ACCESS
    ? ['levels', 'recursos', 'faq']
    : ['features', 'why-housien', 'pricing'];
}

/**
 * Picks the last section whose top has crossed the viewport trigger line.
 *
 * @param sectionIds Ordered section ids to evaluate.
 * @param headerHeight Sticky header height used to offset the trigger line.
 * @returns The active section id, or '' when none has crossed the line.
 */
function computeActiveSection(sectionIds: string[], headerHeight: number): string {
  const triggerLine = headerHeight + window.innerHeight * 0.25;
  let current = '';
  for (const id of sectionIds) {
    const el = document.getElementById(id);
    if (el && el.getBoundingClientRect().top <= triggerLine) current = id;
  }
  return current;
}

/**
 * Reads the sticky header height from the `--header-height` CSS variable,
 * falling back to 72px when it is not set.
 *
 * @returns The header height in pixels.
 */
function readHeaderHeight(): number {
  return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 72;
}

/**
 * Resolves the scrollable element that hosts the landing content, defaulting to
 * `window` when the dedicated content block is not yet in the DOM.
 *
 * @returns The scroll target element or the window.
 */
function resolveScrollTarget(): HTMLElement | Window {
  const cb = document.querySelector('[data-landing-content-block]') as HTMLElement;
  return cb || window;
}

/**
 * Controller returned by {@link createLandingSectionTracker} that lets the hook
 * (re)bind to the scroll target and tear everything down.
 */
interface SectionTracker {
  bind: () => void;
  teardown: () => void;
}

/**
 * Detaches the `scroll` listener from a previously bound target, if any.
 *
 * @param target The currently bound scroll target, or null.
 * @param update The scroll handler to remove.
 * @returns void
 */
function detachScrollTarget(target: HTMLElement | Window | null, update: () => void): void {
  if (target) target.removeEventListener('scroll', update);
}

/**
 * Observes the document body for the landing content block and invokes the
 * callback (then disconnects) once it appears. Does nothing when the block is
 * already present.
 *
 * @param obs The MutationObserver to wire up.
 * @param onAppear Callback invoked when the content block appears.
 * @returns void
 */
function observeContentBlockAppearance(obs: MutationObserver, onAppear: () => void): void {
  if (document.querySelector('[data-landing-content-block]')) return;
  obs.observe(document.body, { childList: true, subtree: true });
}

/**
 * Creates a tracker that binds a scroll listener to the landing content block
 * (or window) and re-binds when the block appears via a MutationObserver.
 *
 * @param update Callback that recomputes and stores the active section.
 * @returns A {@link SectionTracker} with `bind` and `teardown`.
 */
function createLandingSectionTracker(update: () => void): SectionTracker {
  let target: HTMLElement | Window | null = null;
  const bind = (): void => {
    const next = resolveScrollTarget();
    if (next === target) return;
    detachScrollTarget(target, update);
    target = next;
    target.addEventListener('scroll', update, { passive: true });
    update();
  };
  const obs = new MutationObserver(() => {
    if (!document.querySelector('[data-landing-content-block]')) return;
    bind();
    obs.disconnect();
  });
  observeContentBlockAppearance(obs, bind);
  const teardown = (): void => {
    obs.disconnect();
    detachScrollTarget(target, update);
  };
  return { bind, teardown };
}

/**
 * Starts active-section tracking for the landing page: computes the header
 * offset, wires scroll + resize listeners, and returns a cleanup function.
 *
 * @param setActiveSection State setter for the active section id.
 * @returns Cleanup that detaches all listeners and observers.
 */
function startSectionTracking(setActiveSection: (id: string) => void): () => void {
  const sectionIds = resolveSectionIds();
  const headerHeight = readHeaderHeight();
  const update = (): void => setActiveSection(computeActiveSection(sectionIds, headerHeight));
  const tracker = createLandingSectionTracker(update);
  tracker.bind();
  window.addEventListener('resize', update, { passive: true });
  return () => {
    tracker.teardown();
    window.removeEventListener('resize', update);
  };
}

/**
 * Returns the id of the landing section currently scrolled into view (or '' if
 * none / not on the landing page).
 */
export function useActiveSection(isLandingPage: boolean, pathname: string): string {
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    if (!isLandingPage) {
      setActiveSection('');
      return;
    }
    return startSectionTracking(setActiveSection);
  }, [isLandingPage, pathname]);

  return activeSection;
}
